(function () {
  'use strict';

  const identity = value => String(value).trim().toUpperCase();
  function uniqueCodes(values) {
    const seen = new Set();
    return values.map(value => String(value || '').trim()).filter(value => {
      const key = identity(value);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  function discountState(cart) {
    const codes = [];
    const applied = [];
    for (const entry of cart.discount_codes || []) {
      if (!entry || typeof entry.code !== 'string') continue;
      codes.push(entry.code);
      if (entry.applicable === true) applied.push(entry.code);
    }
    function application(value, amount) {
      if (value?.type !== 'discount_code' || !value.title) return;
      codes.push(value.title);
      if (Number(amount) > 0) applied.push(value.title);
    }
    for (const value of cart.cart_level_discount_applications || []) {
      application(value, value.total_allocated_amount);
    }
    for (const item of cart.items || []) {
      for (const allocation of [...(item.line_level_discount_allocations || []), ...(item.discount_allocations || [])]) {
        application(allocation.discount_application, allocation.amount);
      }
    }
    return { codes: uniqueCodes(codes), applied: uniqueCodes(applied) };
  }

  function formatGwpPounds(value) {
    return String(value).replace(/(^|[^\d.,£])(-?\d+(?:[.,]\d{1,2})?)\s*£/g, (_match, prefix, raw, offset, input) => {
      if (/\d\s+$/.test(input.slice(0, offset + prefix.length))) return _match;
      const amount = Number(raw.replace(',', '.'));
      if (!Number.isFinite(amount)) return _match;
      const decimals = Number.isInteger(amount) ? 0 : 2;
      return prefix + new Intl.NumberFormat('en-GB', {
        style: 'currency', currency: 'GBP',
        minimumFractionDigits: decimals, maximumFractionDigits: 2
      }).format(amount);
    });
  }

  async function changeDiscount(io, operation) {
    const code = String(operation.code || '').trim();
    if (!code) return { message: 'empty', confirmed: false };
    if (code.includes(',')) return { message: 'one_code', confirmed: false };
    await io.exclusive(async () => {
      const before = await io.read();
      if (!Array.isArray(before.items)) throw new Error('invalid_cart_response');
      const current = discountState(before).codes;
      const desired = operation.remove
        ? current.filter(value => identity(value) !== identity(code))
        : uniqueCodes([...current, code]);
      const result = await io.write(desired.join(','));
      if (!Array.isArray(result.items)) throw new Error('invalid_cart_response');
      return result;
    });
    await io.settle();
    const cart = await io.read();
    if (!Array.isArray(cart.items)) throw new Error('invalid_cart_response');
    const state = discountState(cart);
    const confirmed = operation.remove
      ? !state.codes.some(value => identity(value) === identity(code))
      : state.applied.some(value => identity(value) === identity(code));
    return { cart, state, confirmed, message: operation.remove
      ? (confirmed ? 'removed' : 'not_removed') : (confirmed ? 'applied' : 'cannot_apply') };
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { uniqueCodes, discountState, changeDiscount, formatGwpPounds };
    return;
  }
  if (window.MBIMarket?.ecommerceEnabled !== true) return;
  const template = document.getElementById('mbi-cart-discounts-template');
  const controller = window.MBICart;
  if (!template || typeof controller?.runExclusive !== 'function') return;
  const root = window.Shopify.routes?.root || '/';
  const views = new Map();
  let cart = null;
  let busy = false;
  let feedback = '';
  let feedbackError = false;
  let readRevision = 0;

  async function request(path, options = {}) {
    const timeout = new AbortController();
    const timer = setTimeout(() => timeout.abort(), 20000);
    try {
      const response = await fetch(root + path, { credentials: 'same-origin', cache: 'no-store',
        ...options, signal: timeout.signal, headers: { Accept: 'application/json', ...options.headers } });
      const value = await response.json();
      if (!response.ok) throw new Error('discount_request_failed');
      return value;
    } finally { clearTimeout(timer); }
  }
  const read = () => request('cart.js');
  const text = (node, value) => { if (node.textContent !== value) node.textContent = value; };
  const panels = () => [...document.querySelectorAll('[data-mbi-discount-view]')];

  function paint(panel) {
    const view = views.get(panel.dataset.mbiDiscountView);
    const input = panel.querySelector('[data-mbi-discount-input]');
    input.value = view.draft;
    const disabled = busy || cart === null || document.documentElement.hasAttribute('data-mbi-cart-syncing');
    panel.querySelectorAll('button, input').forEach(control => { control.disabled = disabled; });
    panel.setAttribute('aria-busy', String(busy));
    const status = panel.querySelector('[data-mbi-discount-status]');
    text(status, panel.dataset[busy ? 'pending' : feedback] || '');
    status.setAttribute('role', feedbackError && !busy ? 'alert' : 'status');
    status.hidden = !status.textContent;
    status.classList.toggle('is-error', feedbackError && !busy);
    status.classList.toggle('is-success', !busy && !feedbackError && (feedback === 'applied' || feedback === 'removed'));
    const state = cart ? discountState(cart) : { codes: [], applied: [] };
    const list = panel.querySelector('[data-mbi-discount-list]');
    const signature = JSON.stringify(state);
    if (list.dataset.signature !== signature) {
      list.replaceChildren();
      for (const code of state.codes) {
        const li = document.createElement('li');
        li.className = 'mbi-cart-discount__code-row';
        const pill = document.createElement('span');
        const isApplied = state.applied.some(value => identity(value) === identity(code));
        pill.className = 'mbi-cart-discount__pill ' + (isApplied ? 'is-applied' : 'is-inactive');
        const label = document.createElement('span');
        text(label, code);
        pill.append(label);
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.mbiDiscountRemove = code;
        button.setAttribute('aria-label', panel.dataset.removeLabel + ' ' + code);
        text(button, '×');
        button.disabled = disabled;
        pill.append(button);
        li.append(pill);
        if (!isApplied) {
          const note = document.createElement('small');
          note.className = 'mbi-cart-discount__code-status';
          note.id = 'mbi-discount-' + panel.dataset.mbiDiscountView + '-inactive-' + list.children.length;
          text(note, panel.dataset.inactive);
          button.setAttribute('aria-describedby', note.id);
          li.append(note);
        }
        list.append(li);
      }
      list.dataset.signature = signature;
    }
    panel.querySelector('[data-mbi-discount-existing]').hidden = state.codes.length === 0;
  }

  function formatRewardMoney() {
    let config;
    try { config = JSON.parse(document.getElementById('gwp-config')?.textContent || '{}'); }
    catch (_error) { return; }
    if (String(config.currency).toUpperCase() !== 'GBP' || config.symbol !== '£') return;
    for (const root of document.querySelectorAll('[data-gwp-root], [data-gwp-gift-root]')) {
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
      let node;
      while ((node = walker.nextNode())) {
        if (node.parentElement?.closest('script, style, textarea, template, noscript')) continue;
        const formatted = formatGwpPounds(node.nodeValue);
        if (formatted !== node.nodeValue) node.nodeValue = formatted;
      }
      for (const element of [root, ...root.querySelectorAll('[title], [aria-label]')]) {
        for (const name of ['title', 'aria-label']) {
          const value = element.getAttribute(name);
          if (!value) continue;
          const formatted = formatGwpPounds(value);
          if (formatted !== value) element.setAttribute(name, formatted);
        }
      }
    }
  }

  function placeRewardTimelines() {
    for (const container of document.querySelectorAll('#cart, #mini-cart')) {
      const summary = container.querySelector('.cart-summary__meta');
      if (!summary) continue;
      const roots = [...container.querySelectorAll('[data-gwp-root]')]
        .filter(root => !root.contains(summary));
      if (!roots.length) continue;
      const anchor = summary.querySelector('[data-mbi-discount-view]')
        || summary.querySelector('.cart-summary__line-items');
      if (!anchor || anchor.parentElement !== summary) continue;
      const siblings = [...summary.children];
      const anchorIndex = siblings.indexOf(anchor);
      const previous = siblings.slice(Math.max(0, anchorIndex - roots.length), anchorIndex);
      if (roots.every((root, index) => previous[index] === root)) continue;
      roots.forEach(root => summary.insertBefore(root, anchor));
    }
  }

  function mount() {
    for (const summary of document.querySelectorAll('#cart .cart-summary__meta, #mini-cart .cart-summary__meta')) {
      const name = summary.closest('#mini-cart') ? 'drawer' : 'basket';
      let panel = summary.querySelector('[data-mbi-discount-view]');
      if (!panel) {
        const view = views.get(name) || { draft: '', open: false };
        views.set(name, view);
        panel = template.content.firstElementChild.cloneNode(true);
        panel.dataset.mbiDiscountView = name;
        panel.open = view.open;
        const input = panel.querySelector('[data-mbi-discount-input]');
        input.id = 'mbi-discount-' + name;
        panel.querySelector('label').htmlFor = input.id;
        const status = panel.querySelector('[data-mbi-discount-status]');
        status.id = 'mbi-discount-status-' + name;
        input.setAttribute('aria-describedby', status.id);
        summary.insertBefore(panel, summary.querySelector('.cart-summary__line-items'));
      }
      paint(panel);
    }
    placeRewardTimelines();
    formatRewardMoney();
  }

  async function sync() {
    const revision = ++readRevision;
    try {
      const fresh = await read();
      if (revision !== readRevision || busy) return;
      if (!Array.isArray(fresh.items)) throw new Error('invalid_cart_response');
      cart = fresh;
      if (feedback === 'unavailable') feedback = '';
    } catch (_error) {
      if (revision !== readRevision || busy) return;
      cart = null;
      feedback = 'unavailable';
      feedbackError = true;
    }
    panels().forEach(paint);
  }

  async function act(panel, operation) {
    if (busy || document.documentElement.hasAttribute('data-mbi-cart-syncing')) return;
    busy = true;
    readRevision += 1;
    feedback = '';
    feedbackError = false;
    panels().forEach(paint);
    try {
      const result = await changeDiscount({ read,
        exclusive: task => controller.runExclusive(task),
        write: discount => request('cart/update.js', { method: 'POST',
          headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ discount }) }),
        settle: () => controller.refresh()
      }, operation);
      if (result.cart) cart = result.cart;
      feedback = result.message;
      feedbackError = !result.confirmed;
      if (result.confirmed && !operation.remove) views.get(panel.dataset.mbiDiscountView).draft = '';
      if (result.confirmed && operation.remove) {
        views.forEach(view => { if (identity(view.draft) === identity(operation.code)) view.draft = ''; });
      }
      document.dispatchEvent(new CustomEvent('mbi:discount-changed', { detail: { confirmed: result.confirmed } }));
    } catch (_error) {
      feedback = 'error';
      feedbackError = true;
      try { cart = await read(); } catch (_readError) { cart = null; }
      await controller.refresh().catch(() => {});
    } finally {
      busy = false;
      mount();
      panels().forEach(paint);
      const current = panels().find(value => value.dataset.mbiDiscountView === panel.dataset.mbiDiscountView);
      if (current?.open) current.querySelector(operation.remove ? 'summary' : '[data-mbi-discount-input]')?.focus({ preventScroll: true });
    }
  }

  document.addEventListener('input', event => {
    const input = event.target.closest?.('[data-mbi-discount-input]');
    const panel = input?.closest('[data-mbi-discount-view]');
    if (panel) views.get(panel.dataset.mbiDiscountView).draft = input.value;
  });
  document.addEventListener('toggle', event => {
    const panel = event.target;
    if (panel.matches?.('[data-mbi-discount-view]')) views.get(panel.dataset.mbiDiscountView).open = panel.open;
  }, true);
  document.addEventListener('click', event => {
    const button = event.target.closest?.('[data-mbi-discount-apply], [data-mbi-discount-remove]');
    const panel = button?.closest('[data-mbi-discount-view]');
    if (!panel) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const remove = button.hasAttribute('data-mbi-discount-remove');
    act(panel, { remove, code: remove ? button.dataset.mbiDiscountRemove : views.get(panel.dataset.mbiDiscountView).draft });
  }, true);
  document.addEventListener('keydown', event => {
    const input = event.target.closest?.('[data-mbi-discount-input]');
    if (event.key !== 'Enter' || !input) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const panel = input.closest('[data-mbi-discount-view]');
    act(panel, { code: input.value });
  }, true);
  document.addEventListener('mbi:cart-rendered', () => { mount(); if (!busy) sync(); });
  document.addEventListener('gwp:cart-changed', () => { if (!busy) sync(); });
  new MutationObserver(mount).observe(document.body, { childList: true, subtree: true, characterData: true });
  mount();
  sync();
})();
